import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { clientApi } from "@/services/api";
import { Button, Card, Modal, StatusBadge, Textarea } from "@/components/ui";

export default function ClientBudgetViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [notes, setNotes] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["client-budget", id],
    queryFn: () => clientApi.getBudget(id!),
    enabled: !!id,
  });

  const budget = data?.data;

  const approveMutation = useMutation({
    mutationFn: () => clientApi.approveBudget(id!, notes),
    onSuccess: () => {
      toast.success("Orçamento aprovado");
      setShowApprove(false);
      setNotes("");
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao aprovar");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => clientApi.rejectBudget(id!, notes),
    onSuccess: () => {
      toast.success("Orçamento recusado");
      setShowReject(false);
      setNotes("");
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao recusar");
    },
  });

  const changesMutation = useMutation({
    mutationFn: () => clientApi.requestChanges(id!, notes),
    onSuccess: () => {
      toast.success("Solicitação enviada");
      setShowChanges(false);
      setNotes("");
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao solicitar alterações");
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!budget) {
    return (
      <Card className="p-6 text-center">
        <p className="text-text-secondary">Orçamento não encontrado.</p>
        <Button className="mt-4" onClick={() => navigate("/client")}>
          Voltar
        </Button>
      </Card>
    );
  }

  const canRespond = ["sent", "viewed", "changes_requested"].includes(
    budget.status,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={() => navigate("/client")}>
            Voltar
          </Button>
          <h1 className="text-2xl font-bold text-text-primary mt-2">
            {budget.number}
          </h1>
          <p className="text-text-secondary">{budget.title || "Sem título"}</p>
        </div>
        <StatusBadge status={budget.status} />
      </div>

      <Card className="p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-text-muted">Empresa</p>
            <p className="text-text-primary font-medium">
              {budget.company_name}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-muted">Valor total</p>
            <p className="text-text-primary font-medium">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(Number(budget.total) || 0)}
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Itens</h2>
        <div className="space-y-3">
          {budget.items?.map((item: any) => (
            <div
              key={item.id}
              className="flex items-center justify-between border-b border-white/5 pb-3"
            >
              <div>
                <p className="text-text-primary font-medium">{item.name}</p>
                <p className="text-xs text-text-muted">
                  {item.quantity} x {item.unit_price}
                </p>
              </div>
              <p className="text-text-secondary">
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(Number(item.total) || 0)}
              </p>
            </div>
          ))}
        </div>
      </Card>

      {canRespond && (
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setShowApprove(true)}>Aprovar</Button>
          <Button variant="secondary" onClick={() => setShowChanges(true)}>
            Solicitar alterações
          </Button>
          <Button variant="danger" onClick={() => setShowReject(true)}>
            Recusar
          </Button>
        </div>
      )}

      <Modal
        isOpen={showApprove}
        onClose={() => setShowApprove(false)}
        title="Aprovar orçamento"
        description="Deseja aprovar este orçamento?"
        size="sm"
      >
        <Textarea
          label="Observações (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setShowApprove(false)}>
            Cancelar
          </Button>
          <Button onClick={() => approveMutation.mutate()}>Confirmar</Button>
        </div>
      </Modal>

      <Modal
        isOpen={showReject}
        onClose={() => setShowReject(false)}
        title="Recusar orçamento"
        size="sm"
      >
        <Textarea
          label="Motivo (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setShowReject(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={() => rejectMutation.mutate()}>
            Recusar
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showChanges}
        onClose={() => setShowChanges(false)}
        title="Solicitar alterações"
        description="Descreva o que precisa ser ajustado."
        size="sm"
      >
        <Textarea
          label="Detalhes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setShowChanges(false)}>
            Cancelar
          </Button>
          <Button onClick={() => changesMutation.mutate()}>Enviar</Button>
        </div>
      </Modal>
    </div>
  );
}
